import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const PAGE_PADDING = 32;
const FOOTER_HEIGHT = 28;

const styles = StyleSheet.create({
  page: {
    padding: PAGE_PADDING,
    paddingBottom: PAGE_PADDING + FOOTER_HEIGHT,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#262626',
  },
  header: {
    marginBottom: 16,
    borderBottom: '2px solid #006EDF',
    paddingBottom: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#006EDF',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: '#595959',
    marginBottom: 2,
  },
  mainSection: {
    marginTop: 14,
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 'bold',
    color: '#006EDF',
    backgroundColor: '#e6f4ff',
    padding: 8,
  },
  blockTitle: {
    marginTop: 10,
    marginBottom: 6,
    fontSize: 11,
    fontWeight: 'bold',
    backgroundColor: '#fafafa',
    border: '1px solid #d9d9d9',
    padding: 6,
  },
  numberCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    border: '1px solid #d9d9d9',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 4,
    marginBottom: 4,
  },
  numberText: {
    fontSize: 8,
    textAlign: 'center',
  },
  itemsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  itemPair: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
    marginBottom: 6,
  },
  listItem: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottom: '1px solid #f0f0f0',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: PAGE_PADDING,
    right: PAGE_PADDING,
    textAlign: 'center',
    fontSize: 8,
    color: '#8c8c8c',
  },
});

interface AnalisePdfDocumentProps {
  result: any;
  modalidadeLabel: string;
  generatedAt: string;
}

function formatPct(value: number, total: number): string {
  if (!total) return '0.00';
  return ((value / total) * 100).toFixed(2);
}

function PageFooter() {
  return (
    <Text
      style={styles.footer}
      render={({ pageNumber, totalPages }) => `TLS Engenharia — Página ${pageNumber} de ${totalPages}`}
      fixed
    />
  );
}

function ListSection({ title, lines }: { title: string; lines: string[] }) {
  if (!lines.length) return null;
  return (
    <>
      <Text style={styles.blockTitle}>{title}</Text>
      {lines.map((line, index) => (
        <Text key={index} style={styles.listItem} wrap>
          {line}
        </Text>
      ))}
    </>
  );
}

function NumberValuePairs({
  items,
  getNumber,
  getValue,
}: {
  items: any[];
  getNumber: (item: any) => number;
  getValue: (item: any) => number | string;
}) {
  if (!items?.length) return null;
  return (
    <View style={styles.itemsGrid}>
      {items.map((item, index) => (
        <View key={index} style={styles.itemPair} wrap={false}>
          <View style={styles.numberCircle}>
            <Text style={styles.numberText}>{getNumber(item)}</Text>
          </View>
          <Text style={styles.numberText}>= {getValue(item)}</Text>
        </View>
      ))}
    </View>
  );
}

function buildCombLines(
  items: any[] | undefined,
  labelKey: string,
  result: any,
  withTotal = true
): string[] {
  if (!items?.length) return [];
  return items
    .filter((item) => item.qtd > 0 || item.qtd_all > 0)
    .map((item) => {
      const label = item[labelKey];
      const pct = formatPct(item.qtd, result.total_comb);
      if (withTotal && item.qtd_all !== undefined) {
        const pctAll = formatPct(item.qtd_all, result.qtd_all);
        return `${label} = ${item.qtd} jogos (${pct} %) | No total existem ${item.qtd_all} (${pctAll} %)`;
      }
      return `${label} = ${item.qtd} jogos (${pct} %)`;
    });
}

export function AnalisePdfDocument({
  result,
  modalidadeLabel,
  generatedAt,
}: AnalisePdfDocumentProps) {
  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header} wrap={false}>
          <Text style={styles.title}>Relatório de Análise de Combinações</Text>
          <Text style={styles.subtitle}>Modalidade: {modalidadeLabel}</Text>
          <Text style={styles.subtitle}>
            Combinações analisadas: {result?.total_comb ?? 0} | Total de combinações possíveis: {result?.qtd_all ?? 0}
          </Text>
          <Text style={styles.subtitle}>Gerado em: {generatedAt}</Text>
        </View>

        <Text style={styles.mainSection}>Análise de números</Text>

        <Text style={styles.blockTitle}>A quantos jogos que não sai</Text>
        <NumberValuePairs
          items={result?.numeros?.[0]?.atraso ?? []}
          getNumber={(item) => item.numero}
          getValue={(item) => item.atraso}
        />

        <Text style={styles.blockTitle}>Vezes que saiu</Text>
        <NumberValuePairs
          items={result?.numeros?.[3]?.vezes_saiu ?? []}
          getNumber={(item) => item.numero}
          getValue={(item) => item.vezes_saiu}
        />

        <PageFooter />
      </Page>

      <Page size="A4" style={styles.page} wrap>
        <Text style={styles.mainSection}>Análise de sorteios</Text>

        <ListSection
          title="Sequências por jogo"
          lines={buildCombLines(result?.combinacoes?.[0]?.sequencias, 'sequencias', result)}
        />
        <ListSection
          title="Sequência direta máxima por jogo"
          lines={buildCombLines(result?.combinacoes?.[1]?.sequencias_dire_max, 'sequencias_dire_max', result)}
        />
        <ListSection
          title="Números pares"
          lines={buildCombLines(result?.combinacoes?.[2]?.num_pares, 'num_pares', result)}
        />
        <ListSection
          title="Números da sequência de Fibonacci"
          lines={buildCombLines(result?.combinacoes?.[3]?.qtd_fibonacci, 'qtd_fibonacci', result)}
        />
        <ListSection
          title="Números primos"
          lines={buildCombLines(result?.combinacoes?.[4]?.qtd_primos, 'qtd_primos', result)}
        />
        <ListSection
          title="Quantidade de linhas"
          lines={buildCombLines(result?.combinacoes?.[5]?.qtd_linhas, 'qtd_linhas', result)}
        />
        <ListSection
          title="Quantidade máxima por linha"
          lines={buildCombLines(result?.combinacoes?.[6]?.qtd_max_linhas, 'qtd_max_linhas', result)}
        />
        <ListSection
          title="Quantidade de colunas"
          lines={buildCombLines(result?.combinacoes?.[7]?.qtd_colunas, 'qtd_colunas', result)}
        />
        <ListSection
          title="Quantidade máxima por coluna"
          lines={buildCombLines(result?.combinacoes?.[8]?.qtd_max_colunas, 'qtd_max_colunas', result)}
        />

        <Text style={styles.blockTitle}>Menor número</Text>
        <NumberValuePairs
          items={(result?.combinacoes?.[9]?.menor_num ?? []).filter((item: any) => item.qtd > 0)}
          getNumber={(item) => item.menor_num}
          getValue={(item) => item.qtd}
        />

        <Text style={styles.blockTitle}>Maior número</Text>
        <NumberValuePairs
          items={(result?.combinacoes?.[10]?.maior_num ?? []).filter((item: any) => item.qtd > 0)}
          getNumber={(item) => item.maior_num}
          getValue={(item) => item.qtd}
        />

        <ListSection
          title="Quantidade de quadrantes utilizados (Divisão em 4 quadrantes)"
          lines={buildCombLines(result?.combinacoes?.[11]?.qtd_quad_em_4_quad, 'qtd_quad_em_4_quad', result)}
        />
        <ListSection
          title="Quantidade máxima por quadrante (Divisão em 4 quadrantes)"
          lines={buildCombLines(result?.combinacoes?.[12]?.qtd_max_quad_em_4_quad, 'qtd_max_quad_em_4_quad', result)}
        />
        <ListSection
          title="Quantidade que repetiu do jogo anterior"
          lines={buildCombLines(result?.combinacoes?.[13]?.qtd_rep_ultimo, 'qtd_rep_ultimo', result, false)}
        />
        <ListSection
          title="Quantidade que repetiu nos 2 jogos anteriores"
          lines={buildCombLines(result?.combinacoes?.[14]?.qtd_rep_2_ultimo, 'qtd_rep_2_ultimo', result, false)}
        />
        <ListSection
          title="Quantidade que repetiu nos 3 jogos anteriores"
          lines={buildCombLines(result?.combinacoes?.[15]?.qtd_rep_3_ultimo, 'qtd_rep_3_ultimo', result, false)}
        />
        <ListSection
          title="Quantidade que repetiu nos 4 jogos anteriores"
          lines={buildCombLines(result?.combinacoes?.[16]?.qtd_rep_4_ultimo, 'qtd_rep_4_ultimo', result, false)}
        />
        <ListSection
          title="Quantidade que repetiu nos 5 jogos anteriores"
          lines={buildCombLines(result?.combinacoes?.[17]?.qtd_rep_5_ultimo, 'qtd_rep_5_ultimo', result, false)}
        />

        <PageFooter />
      </Page>
    </Document>
  );
}

export async function downloadAnalisePdf(
  result: any,
  modalidadeLabel: string
): Promise<void> {
  const { pdf } = await import('@react-pdf/renderer');
  const generatedAt = new Date().toLocaleString('pt-BR');
  const blob = await pdf(
    <AnalisePdfDocument
      result={result}
      modalidadeLabel={modalidadeLabel}
      generatedAt={generatedAt}
    />
  ).toBlob();

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `analise-combinacoes-${modalidadeLabel.toLowerCase().replace(/\s+/g, '-')}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
